import React, { useContext } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

interface XAxis {
  title: string;
  field: string;
  showAxis: boolean;
  format: string;
  tickRotation: number;
}

interface YAxis {
  title: string;
  showAxis: boolean;
  format: string;
  min: number | null;
  max: number | null;
}

interface AxisSelectorProps {}

const AxisSelector = ({}: AxisSelectorProps) => {
  const { config, setConfig } = useContext(GraphConfigFormContext);
  
  if (!config) return null;
  
  const xAxis = config.graph_options.xAxis;
  const yAxisLeft = config.graph_options.yAxis.left;
  const yAxisRight = config.graph_options.yAxis.right;
  // Format options for axes
  const formatOptions = [
    { value: "default", label: "Default" },
    { value: "number", label: "Number" },
    { value: "decimal", label: "Decimal" },
    { value: "percent", label: "Percentage" },
    { value: "currency", label: "Currency" },
    { value: "date", label: "Date" },
    { value: "time", label: "Time" },
    { value: "datetime", label: "Date & Time" },
  ];

  // X-Axis settings
  const handleXAxisChange = (field: keyof XAxis, value: any) => {
    setConfig({
      ...config,
      graph_options: {
        ...config.graph_options,
        xAxis: { ...xAxis, [field]: value },
      },
    });
  };

  // Y-Axis Left settings
  const handleYAxisLeftChange = (field: keyof YAxis, value: any) => {
    setConfig({
      ...config,
      graph_options: {
        ...config.graph_options,
        yAxis: {
          ...config.graph_options.yAxis,
          left: { ...yAxisLeft, [field]: value },
        },
      },
    });
  };

  // Y-Axis Right settings
  const handleYAxisRightChange = (field: keyof YAxis, value: any) => {
    setConfig({
      ...config,
      graph_options: {
        ...config.graph_options,
        yAxis: {
          ...config.graph_options.yAxis,
          right: { ...yAxisRight, [field]: value },
        },
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Label>Axes Configuration</Label>

      <Accordion type="single" collapsible className="w-full">
        {/* X-Axis Configuration */}
        <AccordionItem value="x-axis">
          <AccordionTrigger className="px-4 py-2 bg-muted/40 rounded-md">
            X-Axis
          </AccordionTrigger>
          <AccordionContent className="p-4 border rounded-md mt-2">
            <div className="flex flex-col gap-4">
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  type="text"
                  value={xAxis.title}
                  onChange={(e) => handleXAxisChange("title", e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Show Axis</Label>
                  <Switch
                    checked={xAxis.showAxis}
                    onCheckedChange={(checked) =>
                      handleXAxisChange("showAxis", checked)
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Format</Label>
                  <Select
                    value={xAxis.format}
                    onValueChange={(value) =>
                      handleXAxisChange("format", value)
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {formatOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Tick Rotation (degrees)</Label>
                  <Input
                    type="number"
                    min="-90"
                    max="90"
                    step="5"
                    value={xAxis.tickRotation}
                    onChange={(e) =>
                      handleXAxisChange(
                        "tickRotation",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Y-Axis Left Configuration */}
        <AccordionItem value="y-axis-left">
          <AccordionTrigger className="px-4 py-2 bg-muted/40 rounded-md mt-2">
            Y-Axis (Left)
          </AccordionTrigger>
          <AccordionContent className="p-4 border rounded-md mt-2">
            <div className="flex flex-col gap-4">
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  type="text"
                  value={yAxisLeft.title}
                  onChange={(e) =>
                    handleYAxisLeftChange("title", e.target.value)
                  }
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Show Axis</Label>
                  <Switch
                    checked={yAxisLeft.showAxis}
                    onCheckedChange={(checked) =>
                      handleYAxisLeftChange("showAxis", checked)
                    }
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Format</Label>
                <Select
                  value={yAxisLeft.format}
                  onValueChange={(value) =>
                    handleYAxisLeftChange("format", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {formatOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Min Value</Label>
                  <Input
                    type="number"
                    value={yAxisLeft.min !== null ? yAxisLeft.min : ""}
                    onChange={(e) =>
                      handleYAxisLeftChange(
                        "min",
                        e.target.value === ""
                          ? null
                          : parseFloat(e.target.value)
                      )
                    }
                    placeholder="Auto"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs">Max Value</Label>
                  <Input
                    type="number"
                    value={yAxisLeft.max !== null ? yAxisLeft.max : ""}
                    onChange={(e) =>
                      handleYAxisLeftChange(
                        "max",
                        e.target.value === ""
                          ? null
                          : parseFloat(e.target.value)
                      )
                    }
                    placeholder="Auto"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Y-Axis Right Configuration */}
        <AccordionItem value="y-axis-right">
          <AccordionTrigger className="px-4 py-2 bg-muted/40 rounded-md mt-2">
            Y-Axis (Right)
          </AccordionTrigger>
          <AccordionContent className="p-4 border rounded-md mt-2">
            <div className="flex flex-col gap-4">
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  type="text"
                  value={yAxisRight.title}
                  onChange={(e) =>
                    handleYAxisRightChange("title", e.target.value)
                  }
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Show Axis</Label>
                  <Switch
                    checked={yAxisRight.showAxis}
                    onCheckedChange={(checked) =>
                      handleYAxisRightChange("showAxis", checked)
                    }
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Format</Label>
                <Select
                  value={yAxisRight.format}
                  onValueChange={(value) =>
                    handleYAxisRightChange("format", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {formatOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Min Value</Label>
                  <Input
                    type="number"
                    value={yAxisRight.min !== null ? yAxisRight.min : ""}
                    onChange={(e) =>
                      handleYAxisRightChange(
                        "min",
                        e.target.value === ""
                          ? null
                          : parseFloat(e.target.value)
                      )
                    }
                    placeholder="Auto"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs">Max Value</Label>
                  <Input
                    type="number"
                    value={yAxisRight.max !== null ? yAxisRight.max : ""}
                    onChange={(e) =>
                      handleYAxisRightChange(
                        "max",
                        e.target.value === ""
                          ? null
                          : parseFloat(e.target.value)
                      )
                    }
                    placeholder="Auto"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default AxisSelector;
